import styled from "@emotion/styled";
import { Drawer, ListItemButton } from "@mui/material";
import type { Theme as MuiTheme } from "@mui/material/styles";

import { TOP_BAR_HEIGHT } from "@themes/tokens/sidebar";
import { BaseIconButton } from "@ui/atoms/BaseIconButton/BaseIconButton";

import { COLLAPSED_WIDTH, DRAWER_WIDTH } from "./const";

/**
 * The nav's fixed measurements, from the design: a 264px column (62px as a
 * rail), a 54px logo row, 34px rows and 34px rail tiles. Named so the scope
 * switcher and the user card — which sit in the same column — line up with the
 * menu rows without restating the numbers.
 */
export const SIDEBAR_ROW_HEIGHT = 34;
export const SIDEBAR_CHILD_ROW_HEIGHT = 32;
export const SIDEBAR_CHILD_INDENT = 36;
export const SIDEBAR_RAIL_TILE = 34;
const SIDEBAR_EDGE_BORDER = 1;
const SIDEBAR_RAIL_GROUP_PADDING = 3;
const SIDEBAR_RAIL_ROW_INSET =
  (COLLAPSED_WIDTH - SIDEBAR_EDGE_BORDER - SIDEBAR_RAIL_TILE) / 2;
// The logo row plus its 1px rule is exactly the top bar's height, so the rule
// and the bar's bottom border are one continuous line across the toggle.
export const SIDEBAR_LOGO_ROW_HEIGHT = TOP_BAR_HEIGHT - 1;
export const SIDEBAR_GUTTER = 12;
/**
 * How far the logo's mark sits from the nav's left edge — the SAME distance
 * in both states, so the mark stays exactly put as the column folds.
 *
 * The header's own padding carries whatever the column's gutter does not:
 * expanded the gutter contributes 12 and the header 5, collapsed the gutter
 * is 0 and the header 17. Both animate on one curve, so the two always sum to
 * this and the mark never drifts — it also lands on the rail's centre line
 * (17 + half of the 28px mark = 31 = half of 62).
 */
export const SIDEBAR_LOGO_INSET = 17;

/**
 * The design's own `0.2, 0, 0.2, 1` is a UI-response curve meant for a hover
 * or a press — over 200-odd pixels of travel it front-loads the movement and
 * the fold reads as a snap.
 */
const EASE = "linear";

/**
 * Everything that changes when the nav collapses moves on ONE transition —
 * the drawer's width, the column's padding, each row's padding, and the
 * cross-fade between the wordmark and the mark. The width alone used to
 * animate while the content inside it snapped, which is what read as a jerk
 * rather than a fold.
 */
export const COLLAPSE_MS = 380;

/**
 * A truncated label's reveal on hover. The delay is what keeps a cursor swept
 * down the nav from setting every row it passes in motion.
 */
const MARQUEE_MS = 1400;
const MARQUEE_DELAY_MS = 250;
const MARQUEE_RETURN_MS = 1200;
export const MARQUEE_ATTR = "data-sidebar-marquee";

/**
 * The label fade, which is deliberately NOT the fold's timing.
 *
 * Text sharing the width's 380ms is still legible while the rail is closing
 * over it, so you watch it get clipped. Fading it out over the first 40% and
 * back in only over the last 40% means the rail is always either open enough
 * to hold the word or empty — the eye reads a fold, not a squeeze.
 */
const LABEL_MS = Math.round(COLLAPSE_MS * 0.4);

export const collapseTransition = (theme: MuiTheme, properties: string[]) =>
  theme.transitions.create(properties, {
    duration: COLLAPSE_MS,
    // Not MUI's `sharp`, which is symmetric: it arrives at full speed and
    // stops dead, which is what made the fold feel like it hit the edge.
    easing: EASE
  });

/**
 * The fade for anything that only exists while the nav is open — a row label,
 * a caption, the wordmark. Out early on collapse, in late on expand, so it
 * never plays against the width it is sitting inside.
 */
export const labelTransition = (theme: MuiTheme, visible: boolean) =>
  theme.transitions.create(["opacity"], {
    delay: visible ? COLLAPSE_MS - LABEL_MS : 0,
    duration: LABEL_MS,
    easing: theme.transitions.easing.easeInOut
  });

const openProp = {
  shouldForwardProp: (prop: PropertyKey) => prop !== "open"
};

const SidebarContainer = styled(Drawer)(({ open, theme }) => {
  const width = open ? DRAWER_WIDTH : COLLAPSED_WIDTH;
  const transition = collapseTransition(theme, ["width"]);

  return {
    "& .MuiDrawer-paper": {
      backgroundColor: theme.vars.ui.sidebar.nav.background,
      // A straight column, top to bottom. Explicit, because the theme rounds
      // every Paper.
      borderRadius: 0,
      borderRight: `1px solid ${theme.vars.ui.sidebar.line}`,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      // Visible so the collapse toggle can straddle the nav's edge. The menu
      // scrolls inside its own container, so nothing else can spill.
      overflow: "visible",
      transition,
      width
    },
    flexShrink: 0,
    transition,
    whiteSpace: "nowrap",
    width
  };
});

/** The column inside the drawer: gutters when expanded, centred as a rail. */
const SidebarNav = styled(
  "div",
  openProp
)<{ open: boolean }>(({ open, theme }) => ({
  alignItems: open ? "stretch" : "center",
  boxSizing: "border-box",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  padding: open
    ? `0 ${SIDEBAR_GUTTER}px ${SIDEBAR_GUTTER}px`
    : `0 0 ${SIDEBAR_GUTTER}px`,
  position: "relative",
  transition: collapseTransition(theme, ["padding"])
}));

/**
 * What an open menu does to the column behind it: blur it back and fade it,
 * so the menu is the only live thing on screen. The design's equivalent of a
 * backdrop, without covering the control that opened it — which is why the
 * dimmed region always EXCLUDES that control.
 */
const dimmed = (isDimmed: boolean) => ({
  filter: isDimmed ? "blur(4px)" : "none",
  opacity: isDimmed ? 0.4 : 1,
  pointerEvents: isDimmed ? ("none" as const) : ("auto" as const),
  transition: `filter 180ms ${EASE}, opacity 180ms ${EASE}`
});

/**
 * A label column that gives up its width as the nav folds, rather than
 * unmounting — and the chevron beside it, which does the same.
 *
 * Both the scope switcher's row and the profile card's are this shape, so
 * they are declared once here rather than twice, identically, beside each.
 */
export const FoldingText = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "visible"
})<{ visible?: boolean }>(({ theme, visible = true }) => ({
  display: "flex",
  flex: visible ? 1 : "0 0 0px",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  opacity: visible ? 1 : 0,
  overflow: "hidden",
  transition: [
    collapseTransition(theme, ["flex-basis"]),
    labelTransition(theme, visible)
  ].join(", ")
}));

export const FoldingChevron = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "visible"
})<{ visible?: boolean }>(({ theme, visible = true }) => ({
  color: "rgba(var(--mui-palette-text-primaryChannel) / 0.45)",
  display: "inline-flex",
  flex: "none",
  opacity: visible ? 1 : 0,
  overflow: "hidden",
  transition: [
    collapseTransition(theme, ["width"]),
    labelTransition(theme, visible)
  ].join(", "),
  width: visible ? 12 : 0
}));

/**
 * A region that dims behind an open menu: everything above the user card while
 * the user menu is open, and the menu list alone while the scope switcher is —
 * a narrower region, because the switcher's own card sits above it and has to
 * stay sharp.
 */
const SidebarDimRegion = styled("div", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "dimmed" && prop !== "open"
})<{ dimmed: boolean; open: boolean }>(({ dimmed: isDimmed, open }) => ({
  alignItems: open ? "stretch" : "center",
  alignSelf: "stretch",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  ...dimmed(isDimmed)
}));

const SidebarHeader = styled(
  "div",
  openProp
)<{ open: boolean }>(({ open, theme }) => ({
  alignItems: "center",
  // Spans the column in BOTH states. The column centres its children in the
  // rail, which would shrink this row to its content and leave the padding
  // below positioning nothing — the mark drifted 8px on the fold.
  alignSelf: "stretch",
  display: "flex",
  flex: "none",
  height: SIDEBAR_LOGO_ROW_HEIGHT,
  // Never centred: centring in the rail is what moved the mark. It is always
  // left-aligned at `SIDEBAR_LOGO_INSET` — see the note there.
  justifyContent: "flex-start",
  paddingLeft: open ? SIDEBAR_LOGO_INSET - SIDEBAR_GUTTER : SIDEBAR_LOGO_INSET,
  paddingRight: 0,
  transition: collapseTransition(theme, ["padding"])
}));

/** The 1px rule under the logo. Bleeds into the gutters when expanded. */
const SidebarRule = styled(
  "div",
  openProp
)<{ open: boolean }>(({ open, theme }) => ({
  alignSelf: "stretch",
  backgroundColor: theme.vars.ui.sidebar.nav.line,
  flex: "none",
  height: 1,
  margin: open ? `0 -${SIDEBAR_GUTTER}px 18px` : "0 0 16px",
  transition: collapseTransition(theme, ["margin"])
}));

/** A caption's block when expanded, and the rail's rule when collapsed. */
const CAPTION_HEIGHT = 36;
const RAIL_RULE_HEIGHT = 15;

/**
 * What sits above a group, in both states: the caption when the nav is open,
 * a short rule when it is a rail.
 *
 * One element rather than two swapped ones. They have different heights (36
 * against 15), so exchanging them mid-fold shoved every row below up or down
 * — the biggest of the jumps when closing the nav. Here the block's height
 * animates between the two while the text fades and the rule fades in.
 */
const SidebarGroupSeparator = styled("div", {
  shouldForwardProp: (prop: PropertyKey) =>
    prop !== "open" && prop !== "hasTitle"
})<{ open: boolean; hasTitle: boolean }>(({ hasTitle, open, theme }) => ({
  // The rail's rule, drawn only when collapsed.
  "&::after": {
    backgroundColor: theme.vars.ui.sidebar.nav.line,
    content: '""',
    height: 1,
    left: "50%",
    opacity: open ? 0 : 1,
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%)",
    transition: labelTransition(theme, !open),
    width: SIDEBAR_RAIL_TILE
  },
  alignItems: "flex-end",
  // Spans the column so the rule centres on it — see `SidebarHeader`.
  alignSelf: "stretch",
  boxSizing: "border-box",
  color: theme.vars.ui.sidebar.caption,
  display: "flex",
  flex: "none",
  fontSize: theme.typography.menuCaption.fontSize,
  fontWeight: theme.typography.fontWeightSemiBold,
  height: open ? (hasTitle ? CAPTION_HEIGHT : 0) : RAIL_RULE_HEIGHT,
  justifyContent: open ? "flex-start" : "center",
  letterSpacing: "0.8px",
  lineHeight: 1.2,
  // The caption's own text: gone in the rail, where there is no room to read
  // it and the rule says the same thing.
  opacity: open && hasTitle ? 1 : 0,
  overflow: "hidden",
  padding: open ? "0 10px 6px" : 0,
  position: "relative",
  textTransform: "uppercase",
  transition: collapseTransition(theme, ["height", "opacity", "padding"])
}));

// `&&` outranks the button's own palette.
const SidebarToggleButton = styled(BaseIconButton)(({ theme }) => ({
  "&&": {
    "&:hover": {
      backgroundColor: theme.vars.ui.sidebar.card.hover
    },
    backgroundColor: theme.vars.ui.sidebar.card.background,
    borderColor: theme.vars.ui.sidebar.line,
    boxShadow: "0 1px 4px rgba(3, 26, 22, 0.12)",
    color: theme.vars.ui.sidebar.item.icon,
    position: "absolute",
    right: -(theme.controls.height / 2),
    top: Math.round(SIDEBAR_LOGO_ROW_HEIGHT - theme.controls.height / 2),
    zIndex: 5
  }
}));

interface SidebarRowProps {
  open: boolean;
  active: boolean;
  /** A child of an open group: shorter, and indented past the parent's icon. */
  nested?: boolean;
  // Emotion's `styled` drops MUI's polymorphic `component` typing, so the two
  // props a row-as-Link needs are declared here; ListItemButton forwards both.
  component?: React.ElementType;
  to?: string;
}

/**
 * One menu row. Expanded it is a full-width 34px row; collapsed it is a 34px
 * tile. The selected state is a tint of the accent with a 1px inset ring, in
 * both shapes.
 */
const SidebarRow = styled(ListItemButton, {
  shouldForwardProp: (prop: PropertyKey) =>
    prop !== "open" && prop !== "active" && prop !== "nested"
})<SidebarRowProps>(({ active, nested, open, theme }) => {
  const { item, nav } = theme.vars.ui.sidebar;
  const selected = {
    backgroundColor: item.selected.background,
    boxShadow: `inset 0 0 0 1px ${item.selected.ring}`,
    color: item.selected.text
  };
  // Hovering lifts the label AND its icon to the selected ink, the way the
  // old nav did — the icon's colour is set on its wrapper by
  // `applyIconProps`, so the row has to reach into it to override.
  const hover = {
    "& > span": { color: item.selected.text },
    backgroundColor: nav.hover,
    color: item.selected.text
  };

  return {
    // `&&` doubles the class, out-ranking the app-wide
    // `MuiListItemButton` override in `themes/overrides/menus.ts` — whose
    // hover paints `action.hover`, a LIGHT grey in dark mode. The old nav
    // fought the same rule with `!important`; this is the same win, scoped.
    "&&:not(.Mui-disabled):hover": active ? selected : hover,
    "@media (prefers-reduced-motion: no-preference)": {
      [`&&:not(.Mui-disabled):hover [${MARQUEE_ATTR}]`]: {
        maxWidth: "none",
        transform: "translateX(var(--marquee-distance, 0px))",
        transition: `transform ${MARQUEE_MS}ms ${EASE} ${MARQUEE_DELAY_MS}ms, max-width 0s ${MARQUEE_DELAY_MS}ms`
      }
    },
    ...(!open && {
      "& [data-monogram]": { borderColor: "transparent" }
    }),
    ...(active ? selected : {}),
    alignItems: "center",
    borderRadius: open ? 8 : 10,
    boxSizing: "border-box",
    color: active ? item.selected.text : item.text,
    display: "flex",
    flex: "none",
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightSemiBold,
    gap: open ? 10 : 0,
    height: open
      ? nested
        ? SIDEBAR_CHILD_ROW_HEIGHT
        : SIDEBAR_ROW_HEIGHT
      : SIDEBAR_RAIL_TILE,
    justifyContent: open ? "flex-start" : "center",
    lineHeight: 1,
    // The same override adds a 4px bottom margin to every list button; row
    // spacing here is the list's `gap` alone.
    marginBottom: 0,
    marginLeft: open ? 0 : SIDEBAR_RAIL_ROW_INSET,
    minWidth: 0,
    // Clips the label as the column narrows, so the text slides away behind
    // the icon instead of vanishing on the first frame.
    overflow: "hidden",
    // Centred rather than inset by the glyph width: the inset left a 16px
    // content box, which clipped the wider Monogram tile.
    padding: open ? `0 10px 0 ${nested ? SIDEBAR_CHILD_INDENT : 10}px` : 0,
    position: "relative",
    // Two rhythms, deliberately: the fold is the nav's own (225ms), while
    // hover keeps MUI's short one. `ListItemButton` ships a background-color
    // transition of its own and setting `transition` at all replaces it —
    // which is what turned the hover fade into a snap.
    transition: [
      collapseTransition(theme, ["padding", "width", "gap", "margin-left"]),
      theme.transitions.create(["background-color", "color", "box-shadow"], {
        duration: theme.transitions.duration.shortest
      })
    ].join(", "),
    width: open ? "100%" : SIDEBAR_RAIL_TILE
  };
});

/**
 * Always mounted, even in the rail: it fades and gives up its width rather
 * than unmounting, so collapsing reads as the label sliding out of view.
 */
const SidebarRowLabel = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "visible"
})<{ visible: boolean }>(({ theme, visible }) => ({
  flex: visible ? 1 : "0 0 0px",
  // Room for descenders: the span clips overflow for its ellipsis, and the
  // row's own line-height (1) would cut the tails off a "g" or "p".
  lineHeight: 1.25,
  minWidth: 0,
  opacity: visible ? 1 : 0,
  overflow: "hidden",
  transition: [
    collapseTransition(theme, ["flex-basis"]),
    labelTransition(theme, visible)
  ].join(", "),
  whiteSpace: "nowrap"
}));

/**
 * The label's text, sized to its content and clamped back to the row on the
 * way out: `max-width` restores only AFTER the slide home has finished, so the
 * ellipsis never reappears under moving text. Resting, the clamp makes this
 * exactly the box the label truncated in before.
 */
const SidebarRowLabelText = styled("span")({
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: `transform ${MARQUEE_RETURN_MS}ms ${EASE}, max-width 0s ${MARQUEE_RETURN_MS}ms`,
  whiteSpace: "nowrap",
  width: "max-content"
});

/** The logo's two marks, stacked so one can cross-fade into the other. */
const SidebarLogo = styled("span")<{ style?: React.CSSProperties }>(
  ({ theme }) => ({
    display: "block",
    flex: "none",
    position: "relative",
    transition: collapseTransition(theme, ["width"])
  })
);

const SidebarLogoLayer = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "visible"
})<{ visible: boolean }>(({ theme, visible }) => ({
  display: "block",
  left: 0,
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  position: "absolute",
  top: 0,
  transition: labelTransition(theme, visible)
}));

/** A plain count beside a label ("12"). */
const SidebarRowCount = styled("span")(({ theme }) => ({
  color: theme.vars.ui.sidebar.caption,
  fontSize: theme.typography.caption.fontSize
}));

/** A word beside a label ("Beta"), as a tinted pill. */
const SidebarRowPill = styled("span")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.vars.ui.sidebar.item.selected.background,
  borderRadius: theme.shape.borderRadius,
  boxShadow: `inset 0 0 0 1px ${theme.vars.ui.sidebar.item.selected.ring}`,
  color: theme.vars.ui.sidebar.item.selected.text,
  display: "inline-flex",
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  height: 20,
  lineHeight: 1,
  padding: "0 8px",
  whiteSpace: "nowrap"
}));

const SidebarRowChevron = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "expanded"
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  color: theme.vars.ui.sidebar.caption,
  display: "inline-flex",
  transform: expanded ? "rotate(180deg)" : "none",
  transition: "transform 0.2s"
}));

/** The rail's stand-in for a badge: a dot on the tile's corner. */
const SidebarRailDot = styled("span")(({ theme }) => ({
  backgroundColor: theme.vars.ui.sidebar.item.selected.text,
  borderRadius: 99,
  boxShadow: `0 0 0 2px ${theme.vars.ui.sidebar.nav.background}`,
  height: 8,
  position: "absolute",
  // Inside the tile, not hung off its corner: `SidebarRow` clips its overflow
  // so the label can slide away behind the icon, and a dot at -3 came back as
  // a crescent bitten out of the row's edge.
  right: 5,
  top: 5,
  width: 8
}));

/** A group's rows. Expanded, children indent under the parent (no rail line). */
const SidebarList = styled(
  "ul",
  openProp
)<{ open: boolean }>(({ open }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: open ? 2 : 4,
  listStyle: "none",
  margin: 0,
  padding: 0
}));

/**
 * An open group when collapsed: parent tile and child tiles together in a
 * tinted block, so the grouping stays legible without the labels.
 */
const SidebarRailGroup = styled("div")(({ theme }) => ({
  "&& > *, && li > *": {
    marginLeft: 0
  },
  alignItems: "center",
  backgroundColor: theme.vars.ui.sidebar.nav.group,
  borderRadius: 13,
  boxShadow: `inset 0 0 0 1px ${theme.vars.ui.sidebar.nav.line}`,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginLeft: SIDEBAR_RAIL_ROW_INSET - SIDEBAR_RAIL_GROUP_PADDING,
  padding: SIDEBAR_RAIL_GROUP_PADDING,
  width: "fit-content"
}));

export {
  SidebarContainer,
  SidebarHeader,
  SidebarLogo,
  SidebarLogoLayer,
  SidebarList,
  SidebarNav,
  SidebarDimRegion,
  SidebarGroupSeparator,
  SidebarRailDot,
  SidebarRailGroup,
  SidebarRow,
  SidebarRowChevron,
  SidebarRowCount,
  SidebarRowLabel,
  SidebarRowLabelText,
  SidebarRowPill,
  SidebarRule,
  SidebarToggleButton
};
